/**
 * L'header del portale.
 *
 * Da agosto 2026 non risponde più a «dove posso andare» — quello lo fa la barra
 * laterale (`<SideRail />`). Risponde a **«dove sono e cosa mi riguarda»**: il
 * nome della sezione, lo stato del sistema e le notifiche (per lo staff, che
 * prima li vedeva solo dentro `/admin`), il ruolo con cui stai guardando,
 * l'utente e l'uscita. Il logo è passato in cima alla barra.
 */

import { LayoutDashboard, LogOut, type LucideIcon, Shield } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { HealthStrip } from "@/components/admin/HealthStrip";
import { NotifichePanel } from "@/components/admin/NotifichePanel";
import { VediComeSwitcher } from "@/components/VediComeSwitcher";
import { useFirebaseAuth } from "@/hooks/useFirebaseAuth";
import { useMe } from "@/hooks/useMe";
import { useMyPermessi } from "@/hooks/usePermessi";
import { ADMIN_BREADCRUMB } from "@/lib/admin-nav";
import type { NavZona } from "@/lib/nav-config";
import {
  NAV_AGENTI,
  SEZIONI_AGENTE,
  getVisibleZone,
  isMaitimeStaff,
} from "@/lib/nav-config";

/**
 * Dove sei: nome e icona, dalla rotta.
 *
 * Dal 27-08-2026 porta anche **l'icona**, che è già nella voce di navigazione, e
 * il nome è un po' più grande (richiesta di Enrico). È l'unico posto dove il
 * titolo compare: dentro i moduli lo dice la pillola accesa, e sotto le tab non
 * si ripete più.
 *
 * Mostra il **modulo**, non la sezione — «📋 Commesse» in testa e «Magazzino»
 * nella pillola accesa non ripetono niente e non perdono niente. Perciò le rotte
 * dei figli contano come rotte del padre: `/calendario` e `/partenze` sono
 * sezioni di Spedizioni con un indirizzo proprio, e l'header dice «Spedizioni».
 *
 * ⚠️ Vince il **prefisso più lungo**, non il primo che combacia: `/moduli` è la
 * voce della vetrina e `/moduli/order-readiness` è un modulo. Confrontando i
 * path interi (query compresa) invece delle sole basi vinceva la sezione sul
 * modulo, e l'header diceva «Portafoglio» stando sul Cruscotto — visto in
 * browser, non a mente.
 */
function doveSono(
  pathname: string,
  zone: NavZona[],
): { label: string; icon?: LucideIcon } {
  if (pathname === "/") return { label: "Dashboard", icon: LayoutDashboard };

  if (pathname.startsWith("/admin")) {
    const nome = (() => {
      if (ADMIN_BREADCRUMB[pathname]) return ADMIN_BREADCRUMB[pathname];
      if (pathname.startsWith("/admin/agenti/")) {
        const slug = (pathname.split("/")[3] ?? "").split("-")[0];
        return `${slug.charAt(0).toUpperCase()}${slug.slice(1)}`;
      }
      // Prefisso più lungo che matcha (sotto-pagine con parametri).
      const match = Object.keys(ADMIN_BREADCRUMB)
        .filter((k) => pathname.startsWith(k))
        .sort((a, b) => b.length - a.length)[0];
      return match ? ADMIN_BREADCRUMB[match] : null;
    })();
    return { label: nome ? `Admin · ${nome}` : "Admin", icon: Shield };
  }

  /** Ogni voce di primo livello con **tutte** le basi che le appartengono: la
   *  sua e quelle dei suoi figli. */
  const candidati = zone
    .flatMap((z) => z.voci)
    .flatMap((v) =>
      [v.path, ...(v.children ?? []).map((c) => c.path)]
        .filter((p): p is string => Boolean(p) && p !== "/")
        .map((p) => ({ base: p.split("?")[0], label: v.label, icon: v.icon })),
    )
    // Le sezioni degli agenti non stanno nelle zone (il roster è dinamico), ma
    // «Memoria» è una pagina raggiungibile e senza questa riga l'header era vuoto.
    .concat(
      Object.values(SEZIONI_AGENTE)
        .flat()
        .filter((s) => s.path)
        .map((s) => ({ base: s.path!, label: s.label, icon: NAV_AGENTI.icon })),
    );

  const vinta = candidati
    .filter((c) => pathname === c.base || pathname.startsWith(c.base + "/"))
    .sort((a, b) => b.base.length - a.base.length)[0];

  return { label: vinta?.label ?? "", icon: vinta?.icon };
}

export function TopNav() {
  const { data: me } = useMe();
  const { data: perms } = useMyPermessi();
  const { user: firebaseUser, logout } = useFirebaseAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const staff = isMaitimeStaff(me ?? null);
  const { label: titolo, icon: Icona } = doveSono(
    pathname,
    getVisibleZone(me ?? null, perms),
  );

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b bg-background/95 px-5 backdrop-blur">
      <h1 className="flex min-w-0 items-center gap-2 text-[17px] font-semibold tracking-tight">
        {Icona && <Icona className="size-[18px] flex-none text-brand-700" />}
        <span className="truncate">{titolo}</span>
      </h1>

      <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
        {/* Salute e notifiche erano visibili solo dentro /admin: ora seguono lo
            staff ovunque, che è il punto di avere un header di stato. */}
        {staff && <HealthStrip />}
        {staff && <NotifichePanel />}
        <VediComeSwitcher />
        {firebaseUser?.email && (
          <span className="hidden max-w-[180px] truncate md:inline">{firebaseUser.email}</span>
        )}
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center gap-1.5 rounded px-2 py-1 transition-colors hover:bg-muted"
          title="Esci"
        >
          <LogOut className="size-3.5" />
          <span className="hidden sm:inline">esci</span>
        </button>
      </div>
    </header>
  );
}
